<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';

const props = defineProps({
  contactId: { type: Number, required: true },
  contactName: { type: String, default: '' },
  contactPhone: { type: String, default: '' },
  opportunityId: { type: Number, default: null },
  size: { type: String, default: 'sm' },
});

const store = useStore();
const { currentAccount } = useAccount();
// VoIP só liberado para planos Growth ou superior.
const voipEnabled = computed(() => !!currentAccount.value?.voip_enabled);
const isInitiating = computed(() => store.getters['voipCalls/getUIFlags'].isInitiating);
const hasActiveCall = computed(() => store.getters['voipCalls/hasActiveCall']);
const isRegistered = computed(() => store.getters['voipCalls/isRegistered']);

const handleCall = async () => {
  if (isInitiating.value || hasActiveCall.value || !props.contactPhone) return;
  if (!isRegistered.value) {
    // Try auto-register first
    await store.dispatch('voipCalls/autoRegister');
    if (!store.getters['voipCalls/isRegistered']) return;
  }
  try {
    await store.dispatch('voipCalls/initiateCall', {
      contactId: props.contactId,
      contactName: props.contactName,
      contactPhone: props.contactPhone,
      opportunityId: props.opportunityId,
    });
  } catch (e) {
    // error handled by store
  }
};
</script>

<template>
  <button
    v-if="contactPhone && voipEnabled"
    class="call-btn"
    :class="[size, { disabled: isInitiating || hasActiveCall }]"
    :title="hasActiveCall ? 'Chamada em andamento' : `Ligar para ${contactPhone}`"
    :disabled="isInitiating || hasActiveCall"
    @click.stop="handleCall"
  >
    <svg v-if="!isInitiating" viewBox="0 0 16 16" fill="none" class="call-icon">
      <path d="M3.65 1.4A1.2 1.2 0 0 1 5.3 1.2l1.4 1.4a1.2 1.2 0 0 1 0 1.7c-.4.4-.7 1-.4 1.4.5.8 1.1 1.5 1.8 2.2.7.7 1.4 1.3 2.2 1.8.4.3 1 0 1.4-.4a1.2 1.2 0 0 1 1.7 0l1.4 1.4a1.2 1.2 0 0 1-.2 1.7c-1.2.9-2.6 1.3-3.8.8-1.8-.7-3.5-2-5.2-3.7S2.6 6.1 1.9 4.3C1.4 3.1 1.8 1.7 2.7 1.5z" fill="currentColor"/>
    </svg>
    <span v-else class="loading-dot" />
  </button>
</template>

<style scoped>
.call-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  color: rgb(var(--blue-9));
  opacity: 0.5;
  transition: all 0.2s;
  padding: 0;
  border-radius: 4px;
}
.call-btn:hover:not(.disabled) {
  opacity: 1;
  color: rgb(var(--blue-11));
  background: rgb(var(--blue-3) / 0.4);
}
.call-btn.disabled {
  opacity: 0.2;
  cursor: not-allowed;
}
.call-btn.sm { width: 26px; height: 26px; }
.call-btn.sm .call-icon { width: 14px; height: 14px; }
.call-btn.md { width: 32px; height: 32px; }
.call-btn.md .call-icon { width: 18px; height: 18px; }
.call-btn.lg { width: 40px; height: 40px; }
.call-btn.lg .call-icon { width: 22px; height: 22px; }

.loading-dot {
  width: 4px; height: 4px;
  background: currentColor;
  border-radius: 50%;
  animation: pulse 0.8s infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 0.3; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1.2); }
}
</style>
